import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Course, CourseDetail } from '@/types'

import {
    getCourseBySlug,
    getCourseSlugs,
    getCourses,
    pickRelatedCourses
} from '@/lib/courses'
import { loadPageData, REVALIDATE_SECONDS, RETRY_REVALIDATE_SECONDS } from '@/lib/page-data'
import { Container } from '@/components/generals/container'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { CourseBody } from '@/components/courses/course-body'
import { CourseEnrollCard } from '@/components/courses/course-enroll-card'
import { CourseHero } from '@/components/courses/course-hero'
import { RelatedCourses } from '@/components/courses/related-courses'
import { CtaSection } from '@/components/landing/cta-section'

interface CoursePageProps {
    course: CourseDetail
    related: Array<Course>
}

type CoursePageParams = {
    slug: string
}

interface CoursePageData {
    course: CourseDetail | null
    courses: Array<Course>
}

const CoursePage: FC<CoursePageProps> = ({ course, related }): ReactNode => {
    return (
        <Layout>
            <Seo title={course.title} description={course.summary} ogImage={course.image ?? undefined} />

            <CourseHero course={course} />

            <section id='course-details' aria-label="Course details">
                <Container className='grid gap-10 py-12 sm:py-16 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-16 lg:py-20'>
                    <aside aria-label="Enrollment" className='lg:col-start-2 lg:row-start-1'>
                        <div className='lg:sticky lg:top-24'>
                            <CourseEnrollCard course={course} />
                        </div>
                    </aside>

                    <div className='min-w-0 lg:col-start-1 lg:row-start-1'>
                        <CourseBody course={course} />
                    </div>
                </Container>
            </section>

            <RelatedCourses courses={related} />

            <CtaSection />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths<CoursePageParams> = async () => {
    let slugs: Array<string> = []

    try {
        slugs = await getCourseSlugs()
    }
    catch (error) {
        console.error('[course] Could not list course pages:', error instanceof Error ? error.message : error)
    }

    return {
        paths: slugs.map(slug => ({ params: { slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<CoursePageProps, CoursePageParams> = async ({ params }) => {
    const slug = params?.slug

    if (!slug) return { notFound: true }

    const loaded = await loadPageData<CoursePageData | null>(
        `course:${slug}`,
        async () => {
            const [course, courses] = await Promise.all([getCourseBySlug(slug), getCourses()])

            return { course, courses }
        },
        null
    )

    if (!loaded.data) return { notFound: true, revalidate: RETRY_REVALIDATE_SECONDS }

    const { course, courses } = loaded.data

    if (!course) return { notFound: true, revalidate: REVALIDATE_SECONDS }

    return {
        props: {
            course,
            related: pickRelatedCourses(course, courses)
        },
        revalidate: REVALIDATE_SECONDS
    }
}

export default CoursePage
